import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Alert, Button, message } from 'antd';
import StatusSwitch from 'components/StatusSwitch';
import Empty from 'components/Empty';
import useConfig from './hooks/useConfig';
import { DingTalkFormModal, DingTalkFormValues } from './components/DingTalkForm';
import OutsiteForm from './components/OutsiteForm';
import { Container } from './InsiteConfig';
import { useTransAppProductName } from 'hooks/useTrans';

const StyledEmpty = styled(Empty)`
  padding: 60px 0;
  .${({ theme }) => theme.getAntdPrefixedClassName('empty-description')} {
    margin-top: -4px;
    color: #232426;
  }
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 16px;
  .ant-alert-message {
    > div {
      margin-bottom: 6px;
      font-weight: 500;
    }
    > span {
      font-size: 12px;
      color: #84868c;
    }
  }
`;

type UseConfigReturn = ReturnType<typeof useConfig>;

function getSubmitValues(values: Parameters<UseConfigReturn['updateOutsite']>[0]) {
  const {
    taskId,
    outSiteRemindEnable,
    outSiteRemindViewEnable,
    outSiteRemindViewDelay,
    outSiteRemindViewUnit,
    outSiteRemindSolveEnable,
    outSiteRemindSolveDelay,
    outSiteRemindSolveUnit,
  } = values;

  return {
    taskId,
    outSiteRemindEnable,
    outSiteRemindViewEnable,
    outSiteRemindViewDelay,
    outSiteRemindViewUnit,
    outSiteRemindSolveEnable,
    outSiteRemindSolveDelay,
    outSiteRemindSolveUnit,
  };
}

export interface OutsiteConfigProps {
  config: Remind.Config;
  onUpdateOutsite: UseConfigReturn['updateOutsite'];
  onUpdateDingTalk: UseConfigReturn['updateDingTalk'];
  onDeleteDingTalk: UseConfigReturn['deleteDingTalk'];
  modal: ModalReturnType<DingTalkFormValues>;
}

function OutsiteConfig(props: OutsiteConfigProps) {
  const {
    config,
    onUpdateOutsite,
    onUpdateDingTalk,
    onDeleteDingTalk,
    modal: { show, hide, meta, visible },
  } = props;

  const { taskId, dingtalkConfig, feishuConfig } = config;

  const initialValues = useMemo(() => getSubmitValues(config), [config]);

  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const enable = useMemo(() => {
    const { outSiteRemindEnable } = config;

    return (
      <StatusSwitch
        checked={outSiteRemindEnable}
        onChange={(outSiteRemindEnable) => {
          return onUpdateOutsite({
            ...getSubmitValues(config),
            outSiteRemindEnable,
          })
            .then(() => {
              message.success(
                outSiteRemindEnable
                  ? t('remind.站外提醒开启成功')
                  : t('remind.站外提醒已关闭，之后不再进行站外提醒'),
              );
            })
            .catch(() => {
              message.error(t('remind.网络错误，请稍后再试'));
            });
        }}
        checkedChildren={t('remind.开')}
        unCheckedChildren={t('remind.关')}
      />
    );
  }, [config, onUpdateOutsite, t]);

  const dingTalkFormModal = meta && (
    <DingTalkFormModal
      visible={visible}
      initialValues={meta}
      onSubmit={(values, helpers) => {
        return onUpdateDingTalk(values)
          .then(() => {
            message.success(t('operation.saveSucceed'));

            hide();

            helpers.resetForm();
          })
          .catch(() => {
            message.error(t('remind.网络错误，请稍后再试'));
          });
      }}
      onDelete={() => onDeleteDingTalk({ taskId, type: meta.type })}
      onCancel={() => hide()}
    />
  );

  if (!dingtalkConfig && !feishuConfig) {
    return (
      <Container>
        <StyledEmpty description={t('remind.还未绑定站外提醒平台')}>
          <Button
            type="primary"
            onClick={() =>
              show({
                taskId,
                type: 0,
                webhook: '',
                secret: '',
              })
            }
          >
            {t('去绑定')}
          </Button>
          {dingTalkFormModal}
        </StyledEmpty>
      </Container>
    );
  }

  const alert = (
    <StyledAlert
      banner
      showIcon={false}
      type="info"
      message={
        <>
          <div>
            {t('remind.站外提醒开关')}：{enable}
          </div>
          <span>{appProductNameT('remind.outsiteOpenAlertMessage')}</span>
        </>
      }
    />
  );

  const outsiteForm = (
    <OutsiteForm
      type={feishuConfig ? 1 : 0}
      initialValues={initialValues}
      onSubmit={(values, helpers) => {
        return onUpdateOutsite(getSubmitValues(values))
          .then(() => {
            message.success(t('operation.saveSucceed'));

            helpers.resetForm();
          })
          .catch(() => {
            message.error(t('remind.网络错误，请稍后再试'));
          });
      }}
    >
      {(form, formProps) => {
        const { dirty, isSubmitting, handleSubmit } = formProps;

        return (
          <>
            {form}
            <footer>
              <Button
                type="primary"
                disabled={!dirty}
                loading={isSubmitting}
                onClick={() => handleSubmit()}
              >
                {t('remind.保存')}
              </Button>
            </footer>
          </>
        );
      }}
    </OutsiteForm>
  );

  return (
    <Container>
      {dingTalkFormModal}
      {alert}
      {outsiteForm}
    </Container>
  );
}

export default OutsiteConfig;
